package com.siriusxm.pia.views.unifiedaggregator.dataflow

import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*

/**
 * StyleSheet for the Data Flow visualization components.
 * Defines styles for the three-column layout, nodes, and connection states.
 */
object DataFlowStyles : StyleSheet() {

    /**
     * Main container for the entire data flow visualization.
     * Uses relative positioning to allow the SVG overlay to be absolutely positioned.
     */
    val container by style {
        position(Position.Relative)
        // Establish a stacking context (relative + z-index) so the internal column
        // (z-index 1) and SVG connection overlay (z-index 10) are confined to this
        // container instead of leaking into the root stacking context, where they
        // would otherwise render above the header bar and its services popup menu.
        property("z-index", "0")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        gap(3.em)
        minHeight(600.px)
        overflow("hidden")
    }

    /**
     * Individual column container for producers, types, or consumers.
     * Uses flexbox to create a fixed header with scrolling content area.
     */
    val column by style {
        position(Position.Relative)
        property("z-index", "1")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        flex(1)
        minWidth(200.px)
        maxHeight(700.px)
        padding(0.5.em)
        overflow("hidden")
    }

    /**
     * Header for each column showing the category name.
     * Fixed at the top of the column, centered.
     */
    val columnHeader by style {
        property("flex-shrink", "0")
        fontSize(1.1.em)
        fontWeight(600)
        property("text-transform", "uppercase")
        letterSpacing(0.05.em)
        color(SXMUI.textColor.value())
        textAlign("center")
    }

    /**
     * Wrapper for the scrollable content area that provides gradient overlays
     * to indicate more content above/below.
     */
    val columnContentWrapper by style {
        flex(1)
        position(Position.Relative)
        overflow("hidden")
    }

    /**
     * Top gradient overlay to indicate more content above.
     */
    val columnGradientTop by style {
        position(Position.Absolute)
        property("top", "0")
        property("left", "0")
        property("right", "0")
        height(30.px)
        property("background", "linear-gradient(to bottom, ${SXMUI.mainLayoutBackgroundColor.value()}, transparent)")
        property("pointer-events", "none")
        property("z-index", "2")
    }

    /**
     * Bottom gradient overlay to indicate more content below.
     */
    val columnGradientBottom by style {
        position(Position.Absolute)
        property("bottom", "0")
        property("left", "0")
        property("right", "0")
        height(30.px)
        property("background", "linear-gradient(to top, ${SXMUI.mainLayoutBackgroundColor.value()}, transparent)")
        property("pointer-events", "none")
        property("z-index", "2")
    }

    /**
     * Scrollable content area within each column.
     * Contains the list of nodes and scrolls independently of the header.
     * Includes padding to prevent clipping of hover/selection effects.
     * Scrollbars are hidden but scrolling is still enabled.
     */
    val columnContent by style {
        property("height", "100%")
        overflowY("auto")
        padding(30.px, 0.75.em)
        // Hide scrollbar for Chrome, Safari, and Opera
        property("-ms-overflow-style", "none")  // IE and Edge
        property("scrollbar-width", "none")  // Firefox

        self + selector("::-webkit-scrollbar") style {
            property("display", "none")
        }
    }

    /**
     * Individual node representing a producer, type, or consumer.
     * Styled as a clickable card with hover and selection states.
     */
    val node by style {
        padding(0.6.em, 0.8.em)
        marginBottom(0.4.em)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        cursor("pointer")
        property("transition", "all 0.15s ease")
        fontSize(0.85.em)

        self + hover style {
            backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
            property("transform", "translateX(2px)")
        }
    }

    /**
     * Node in selected state - the primary selected item.
     */
    val nodeSelected by style {
        backgroundColor(SXMUI.selectionBackgroundColor.value())
        property("border-color", SXMUI.brandColor.value().toString())
        property("box-shadow", "0 0 0 1px ${SXMUI.brandColor.value()}")
    }

    /**
     * Node in highlighted state - related to the selected item (direct type relationship).
     */
    val nodeHighlighted by style {
        backgroundColor(SXMUI.selectionBackgroundColor.value())
        property("border-color", SXMUI.brandColor.value().toString())
        opacity(0.9)
    }

    /**
     * Node in partial-highlighted state - can publish partials to the selected type.
     * Uses a distinct color (highlight/magenta) to differentiate from direct producers.
     */
    val nodePartialHighlighted by style {
        backgroundColor(Color("#FCE4F3"))
        property("border-color", SXMUI.highlightColor.value().toString())
        opacity(0.9)
    }

    /**
     * Node in dimmed state - not related to the selection.
     */
    val nodeDimmed by style {
        opacity(0.3)
    }

    /**
     * Node name/title text styling.
     */
    val nodeName by style {
        fontWeight(500)
        color(SXMUI.textColor.value())
        property("word-break", "break-word")
    }

    /**
     * Node ID or secondary text styling.
     */
    val nodeId by style {
        fontSize(0.75.em)
        color(SXMUI.subtleTextColor.value())
        marginTop(0.2.em)
    }

    /**
     * Disabled indicator badge for producers/consumers that are disabled.
     */
    val disabledBadge by style {
        display(DisplayStyle.InlineBlock)
        fontSize(0.65.em)
        backgroundColor(SXMUI.messageWarnBackground.value())
        color(SXMUI.messageWarnForeground.value())
        padding(0.15.em, 0.4.em)
        borderRadius(3.px)
        marginLeft(0.5.em)
        fontWeight(500)
    }

    /**
     * Empty state message when no items exist in a column.
     */
    val emptyState by style {
        color(SXMUI.subtleTextColor.value())
        fontStyle("italic")
        padding(1.em)
        textAlign("center")
    }

    /**
     * The middle column (types) - styles specific to the center column.
     * The actual background is now on columnContent.
     */
    val typesColumn by style {
        // Types column uses the same base styling as other columns
    }
}
